import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * useSubscribe
 *  - userId: id interne
 *  - onStatusChange: cb(status)
 *
 * Methods:
 *  - subscribe(payload): triggers backend /v1/payments
 *
 * Backend response is expected:
 *  { paymentId, status, clientSecret?, checkoutUrl? }
 *
 */
export default function useSubscribe({ userId, onStatusChange } = {}) {
    const wsRef = useRef(null);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const pollingRef = useRef(null);

    useEffect(() => {
        // try to open websocket on mount
        try {
            const protocol = window.location.protocol === "https:" ? "wss" : "ws";
            const host = window.location.host;
            const wsUrl = `${protocol}://${host}/ws-payments/websocket`;
            wsRef.current = new WebSocket(wsUrl);

            wsRef.current.onopen = () => {
                console.debug("WS connected");
            };
            wsRef.current.onmessage = (ev) => {
                try {
                    const msg = JSON.parse(ev.data);
                    // expected { type: 'payment.updated', paymentId: '...', status: 'SUCCEEDED', payload: {...} }
                    if (msg.type === "payment.updated") {
                        setStatus(msg.status);
                        onStatusChange && onStatusChange(msg.status);
                    }
                } catch (e) {
                    console.error("WS parse error", e);
                }
            };
            wsRef.current.onclose = () => {
                console.debug("WS closed");
            };
            wsRef.current.onerror = (err) => {
                console.debug("WS error", err);
            };
        } catch (e) {
            console.debug("WS init failed", e);
        }

        return () => {
            if (wsRef.current) wsRef.current.close();
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, []);

    async function subscribe(payload) {
        setError(null);
        setStatus("CREATING");
        try {
            // Add userId
            const body = { ...payload, payerId: userId };

            const resp = await fetch("/v1/payments", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (!resp.ok) {
                const txt = await resp.text();
                throw new Error(txt || "Erreur backend");
            }

            const data = await resp.json();
            // data = { paymentId, status, clientSecret?, checkoutUrl? }

            setStatus(data.status);
            onStatusChange && onStatusChange(data.status);

            // If Checkout URL returned (Paypal or Stripe Checkout), redirect
            if (data.checkoutUrl) {
                window.location.href = data.checkoutUrl;
                return { paymentId: data.paymentId, status: data.status };
            }

            // If clientSecret returned (SetupIntent flow), confirm it on client - but
            // the hook caller usually handles confirm with stripe.confirmCardSetup.
            // We'll expose the clientSecret in response.
            // If neither, we start polling fallback to track status
            if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
                // start polling fallback: GET /v1/payments/{paymentId}
                pollingRef.current = setInterval(async () => {
                    try {
                        const s = await fetch(`/v1/payments/${data.paymentId}`).then((r) => r.json());
                        if (s.status && s.status !== status) {
                            setStatus(s.status);
                            onStatusChange && onStatusChange(s.status);
                            if (s.status === "SUCCEEDED" || s.status === "FAILED") {
                                clearInterval(pollingRef.current);
                            }
                        }
                    } catch (err) {
                        console.debug("poll error", err);
                    }
                }, 3000);
            }

            return data;
        } catch (err) {
            console.error(err);
            setError(err.message || "Erreur lors de la souscription");
            setStatus("ERROR");
            return { error: err };
        }
    }

    return { subscribe, status, error };
}
